import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Plus, Filter, ChevronRight, Sparkles, Download, AlertCircle, Users, Clock, FileText, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { GlassCard, SectionLabel, SeverityBadge, StatusBadge, ConfidenceBar, GradientButton, GhostButton } from "@/components/primitives";
import Markdown from "@/components/Markdown";
import EnterpriseMemory from "@/components/EnterpriseMemory";
import StructuredRCA from "@/components/StructuredRCA";

const SEVS = ["all","critical","high","medium","low"];
const STAT = ["all","open","investigating","mitigated","resolved"];

export default function Incidents() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [sev, setSev] = useState("all");
  const [stat, setStat] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (sev !== "all") params.severity = sev;
    if (stat !== "all") params.status = stat;
    const { data } = await api.get("/incidents", { params });
    setList(data);
  };
  useEffect(() => { load(); }, [q, sev, stat]); // eslint-disable-line

  return (
    <div className="space-y-5" data-testid="incidents-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <SectionLabel>Incident Intelligence</SectionLabel>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Triage with <span className="gradient-text">AI memory</span></h1>
        </div>
        <GradientButton data-testid="incidents-create" onClick={() => setShowNew(true)}><Plus className="h-4 w-4" /> New Incident</GradientButton>
      </div>

      <GlassCard className="p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <input data-testid="incidents-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search incidents…" className="w-full pl-9 pr-3 py-2 bg-white/[0.04] border border-white/10 rounded-xl text-sm focus:outline-none focus:border-cyan-400/50" />
        </div>
        <Pill label="Severity" value={sev} options={SEVS} onChange={setSev} testid="incidents-filter-severity" />
        <Pill label="Status" value={stat} options={STAT} onChange={setStat} testid="incidents-filter-status" />
      </GlassCard>

      <div className="grid lg:grid-cols-2 gap-4">
        {list.map((it, i) => (
          <motion.div key={it.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
            <Link to={`/app/incidents/${it.id}`} data-testid={`incident-card-${it.id}`}>
              <GlassCard className="p-5 cursor-pointer">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <SeverityBadge value={it.severity} />
                      <StatusBadge value={it.status} />
                      <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{it.service}</span>
                    </div>
                    <h3 className="mt-2 text-base font-semibold truncate">{it.title}</h3>
                    <p className="text-sm text-slate-400 line-clamp-2 mt-1">{it.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-500 mt-1" />
                </div>
                <div className="mt-4"><ConfidenceBar value={it.ai_confidence || 0.85} /></div>
                <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                  <span><Users className="inline h-3 w-3 mr-1" /> {it.assignee || "Unassigned"}</span>
                  <span className="font-mono text-cyan-300">Risk {it.risk_score}</span>
                </div>
              </GlassCard>
            </Link>
          </motion.div>
        ))}
        {list.length === 0 && <div className="text-slate-400 text-sm p-8 text-center col-span-full">No incidents match your filters.</div>}
      </div>

      {showNew && <NewIncidentDialog onClose={() => setShowNew(false)} onCreated={(i) => { setShowNew(false); nav(`/app/incidents/${i.id}`); }} />}
    </div>
  );
}

function Pill({ label, value, options, onChange, testid }) {
  return (
    <div className="relative">
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white/[0.04] border border-white/10 rounded-xl text-sm px-3 py-2 pr-7 focus:outline-none">
        {options.map((o) => <option key={o} value={o} className="bg-slate-900">{`${label}: ${o}`}</option>)}
      </select>
    </div>
  );
}

function NewIncidentDialog({ onClose, onCreated }) {
  const [data, setData] = useState({ title: "", description: "", severity: "medium", service: "payments-api", department: "Engineering", tags: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data: created } = await api.post("/incidents", { ...data, tags: data.tags.split(",").map((s) => s.trim()).filter(Boolean) });
      onCreated(created);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4" onClick={onClose}>
      <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg glass p-6" data-testid="new-incident-dialog">
        <h3 className="text-lg font-bold">Create Incident</h3>
        <div className="mt-4 space-y-3">
          <input required value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} placeholder="Title" className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="new-incident-title" />
          <textarea required value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} placeholder="Description" rows={4} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="new-incident-description" />
          <div className="grid grid-cols-2 gap-3">
            <select value={data.severity} onChange={(e) => setData({ ...data, severity: e.target.value })} className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid="new-incident-severity">
              {["critical","high","medium","low"].map((s) => <option key={s} className="bg-slate-900" value={s}>{s.toUpperCase()}</option>)}
            </select>
            <input value={data.service} onChange={(e) => setData({ ...data, service: e.target.value })} placeholder="Service" className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm" />
          </div>
          <input value={data.tags} onChange={(e) => setData({ ...data, tags: e.target.value })} placeholder="Tags (comma separated)" className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <GradientButton type="submit" disabled={busy} data-testid="new-incident-submit">{busy ? "Creating…" : "Create"}</GradientButton>
        </div>
      </motion.form>
    </div>
  );
}

// -------- Incident detail (Enterprise Memory Engine) --------
export function IncidentDetail() {
  const { id } = useParams();
  const [it, setIt] = useState(null);
  const [rcaLoading, setRcaLoading] = useState(false);

  const load = () => {
    api.get(`/incidents/${id}`).then((r) => setIt(r.data)).catch(() => {});
  };
  useEffect(load, [id]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("incident:refresh", h);
    return () => window.removeEventListener("incident:refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const generateRCA = async () => {
    setRcaLoading(true);
    try { const { data } = await api.post(`/incidents/${id}/rca`); setIt((p) => ({ ...p, rca: data.rca })); } finally { setRcaLoading(false); }
  };

  const updateStatus = async (status) => {
    const { data } = await api.patch(`/incidents/${id}`, { status });
    setIt(data);
  };

  const downloadReport = () => {
    const text = `# Incident Report — ${it.title}\n\nSeverity: ${it.severity}\nStatus: ${it.status}\nService: ${it.service}\nReporter: ${it.reporter}\n\n## Description\n${it.description}\n\n## Root Cause Analysis\n${it.rca || "Not generated"}\n\n## Resolution\n${it.resolution || "Pending"}\n\nGenerated by SentinelAI on ${new Date().toISOString()}`;
    const blob = new Blob([text], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `incident-${it.id}.md`; a.click();
  };

  if (!it) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500"><Link to="/app/incidents" className="hover:text-cyan-300">Incidents</Link> / {it.id.slice(0, 8)}</div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight max-w-3xl">{it.title}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <SeverityBadge value={it.severity} />
            <StatusBadge value={it.status} />
            <span className="text-xs text-slate-400 font-mono">{it.service} · {it.department}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <GhostButton onClick={downloadReport} data-testid="incident-download"><Download className="h-4 w-4" /> Report</GhostButton>
          <GradientButton onClick={generateRCA} disabled={rcaLoading} data-testid="incident-generate-rca"><Sparkles className="h-4 w-4" /> {rcaLoading ? "Analyzing…" : "Generate RCA"}</GradientButton>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <GlassCard className="p-6">
            <SectionLabel>Overview</SectionLabel>
            <p className="mt-3 text-slate-300 leading-relaxed">{it.description}</p>

            <div className="mt-6 grid grid-cols-3 gap-3">
              <Metric label="AI Confidence" value={`${Math.round((it.ai_confidence || 0.8) * 100)}%`} />
              <Metric label="Risk Score" value={it.risk_score} />
              <Metric label="Reporter" value={it.reporter || "system"} />
            </div>
            <div className="mt-6"><ConfidenceBar value={it.ai_confidence || 0.85} /></div>

            {it.rca && (
              <div className="mt-6">
                <SectionLabel>Quick RCA</SectionLabel>
                <div className="mt-3 p-4 rounded-2xl bg-white/[0.03] border border-white/10" data-testid="incident-rca-block">
                  <Markdown source={it.rca} />
                </div>
              </div>
            )}

            <div className="mt-6">
              <SectionLabel>Timeline</SectionLabel>
              <ol className="relative mt-3 border-l border-white/10 ml-2">
                {(it.timeline || []).map((t, i) => (
                  <li key={i} className="ml-4 pb-3">
                    <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-cyan-400 shadow-[0_0_12px_rgba(0,240,255,0.6)]" />
                    <div className="text-sm">{t.event}</div>
                    <div className="text-[10px] font-mono text-slate-500">{t.actor} · {new Date(t.ts).toLocaleString()}</div>
                  </li>
                ))}
              </ol>
            </div>
          </GlassCard>

          <StructuredRCA incident={it} />
        </div>

        <div className="space-y-5">
          <GlassCard className="p-6">
            <SectionLabel>Status & Actions</SectionLabel>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {["open","investigating","mitigated","resolved"].map((s) => (
                <button key={s} data-testid={`incident-set-${s}`} onClick={() => updateStatus(s)} className={`text-xs px-3 py-2 rounded-lg border ${it.status === s ? "bg-cyan-500/10 border-cyan-400/40 text-cyan-200" : "bg-white/[0.03] border-white/10 hover:border-white/20"}`}>{s}</button>
              ))}
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-300"><Users className="h-4 w-4 text-cyan-300" /> Assigned to <span className="ml-auto font-mono text-xs">{it.assignee}</span></div>
              <div className="flex items-center gap-2 text-slate-300"><Clock className="h-4 w-4 text-cyan-300" /> Updated <span className="ml-auto font-mono text-xs">{new Date(it.updated_at).toLocaleString()}</span></div>
              <div className="flex items-center gap-2 text-slate-300"><ShieldCheck className="h-4 w-4 text-cyan-300" /> Escalation L2 → On-call</div>
            </div>
          </GlassCard>

          <EnterpriseMemory incidentId={it.id} />
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-lg font-semibold">{value}</div>
    </div>
  );
}
